import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useGameStore } from "../../store/gameStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Swords, MessageCircleWarning } from "lucide-react";
import TriggerBadge from "./TriggerBadge";

// Companion to TriggerBadge: an NPC's turn came up in combat and it doesn't have Auto-Attack on,
// so — instead of silently auto-rolling — the threatened player (and the GM, as a backstop) get a
// square notification they can resolve on their own time, or hand off to the GM to narrate by hand.
export default function NpcAttackModal({ gameId, myMemberId, isGm }) {
  const pending = useGameStore((s) => s.pendingNpcAttack);
  const setPending = useGameStore((s) => s.setPendingNpcAttack);
  const sendWs = useGameStore((s) => s.sendWs);
  const [acknowledged, setAcknowledged] = useState(false);
  const [resolving, setResolving] = useState(false);

  const relevant = pending && (pending.target_member_id === myMemberId || isGm);

  useEffect(() => { setAcknowledged(false); }, [pending?.pending_id]);

  if (!relevant) return null;

  const resolve = async () => {
    setResolving(true);
    try {
      await api.post(`/games/${gameId}/combat/resolve-pending-attack`, { npc_token_id: pending.npc_token_id });
      setPending(null);
      setAcknowledged(false);
    } catch {
      toast.error("Couldn't resolve the attack");
    } finally {
      setResolving(false);
    }
  };

  const narrate = () => {
    sendWs({ type: "talk_to_gm_ping", data: { member_id: myMemberId, member_name: "A player", label: `let the GM narrate ${pending.npc_name}'s attack` } });
    api.post(`/games/${gameId}/combat/dismiss-pending-attack`, { npc_token_id: pending.npc_token_id }).catch(() => {});
    setPending(null);
    setAcknowledged(false);
  };

  return (
    <>
      {!acknowledged && (
        <TriggerBadge testId="npc-attack-badge" label={pending.label} onClick={() => setAcknowledged(true)} />
      )}
      <Dialog open={acknowledged} onOpenChange={(v) => !v && setAcknowledged(false)}>
        <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-sm" data-testid="npc-attack-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Swords className="w-5 h-5" /> Incoming Attack</DialogTitle>
            <DialogDescription>{pending.label}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Button data-testid="npc-attack-resolve-button" onClick={resolve} disabled={resolving} className="w-full rounded-full gap-2 bg-[#D90429] hover:bg-[#b8031f]">
              <Swords className="w-4 h-4" /> Take the Hit
            </Button>
            <Button data-testid="npc-attack-narrate-button" onClick={narrate} disabled={resolving} variant="secondary" className="w-full rounded-full gap-2">
              <MessageCircleWarning className="w-4 h-4" /> Let the GM Narrate
            </Button>
            {isGm && (
              <button data-testid="npc-attack-dismiss-button" onClick={() => { api.post(`/games/${gameId}/combat/dismiss-pending-attack`, { npc_token_id: pending.npc_token_id }).catch(() => {}); setPending(null); setAcknowledged(false); }} className="w-full text-center text-xs text-[#f1e0c5]/40 hover:text-white pt-1">
                Dismiss
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
